import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, UserRole } from '../types';
import { authService } from '../services/authService';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
  canAccess: (resource: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const rolePermissions: Record<UserRole, string[]> = {
  admin: [
    // Admin a accès à TOUT
    'users:*', 'teams:*', 'products:*', 'suppliers:*', 'orders:*', 
    'chantiers:*', 'ventes:*', 'achats:*', 'retours:*', 'magasins:*', 
    'impressions:*', 'audit:*', 'import_export:*', 'locations:*'
  ],
  responsable_bureau: [
    // Gestion administrative et commerciale
    'users:read', 'users:write',
    'products:read', 'products:write',
    'suppliers:read', 'suppliers:write',
    'chantiers:read', 'chantiers:write',
    'ventes:read', 'ventes:write', 'ventes:validate',
    'achats:read', 'achats:write', 'achats:validate',
    'retours:read', 'retours:validate',
    'magasins:read', 'magasins:inventory',
    'impressions:read', 'impressions:generate',
    'import_export:read', 'import_export:write'
  ],
  responsable_depot: [
    // Gestion complète du stock et des opérations
    'products:read', 'products:write', 'products:adjust_stock',
    'suppliers:read', 'suppliers:write',
    'chantiers:read', 'chantiers:write',
    'ventes:read', 'ventes:write', 'ventes:validate',
    'achats:read', 'achats:write', 'achats:validate', 'achats:receive',
    'retours:read', 'retours:write', 'retours:validate',
    'magasins:read', 'magasins:write', 'magasins:inventory',
    'locations:read', 'locations:write',
    'impressions:read', 'impressions:generate',
    'import_export:read', 'import_export:write'
  ],
  responsable_equipe: [
    // Gestion de son équipe et chantiers
    'products:read',
    'chantiers:read', 'chantiers:write_own',
    'ventes:read', 'ventes:write_own',
    'achats:read', 'achats:receive',
    'retours:read', 'retours:write_own',
    'impressions:read'
  ],
  charpentier: [
    // Consultation et commandes pour son travail
    'products:read',
    'chantiers:read_own',
    'ventes:read_own', 'ventes:write_own',
    'retours:read_own',
    'impressions:read'
  ],
  stagiaire: [
    // Consultation uniquement
    'products:read',
    'chantiers:read_limited',
    'ventes:read_limited',
    'impressions:read'
  ]
};

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      // Simulate token validation
      try {
        const userData = authService.validateToken(token);
        setUser(userData);
      } catch (error) {
        localStorage.removeItem('authToken');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const { user: userData, token } = await authService.login(email, password);
      localStorage.setItem('authToken', token);
      setUser(userData);
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    setUser(null);
  };

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    return rolePermissions[user.role]?.includes(permission) || false;
  };

  const canAccess = (resource: string): boolean => {
    if (!user) return false;
    
    // L'ADMIN a accès à TOUT sans exception
    if (user.role === 'admin') return true;
    
    switch (resource) {
      case 'users':
        return hasPermission('users:read');
      case 'audit':
        return hasPermission('audit:read');
      case 'magasins':
        return hasPermission('magasins:read');
      case 'locations':
        return hasPermission('locations:read');
      case 'import_export':
        return hasPermission('import_export:read');
      case 'achats':
        return hasPermission('achats:read');
      case 'retours':
        return hasPermission('retours:read');
      default:
        return hasPermission(`${resource}:read`);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, hasPermission, canAccess }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}